import React, { useState, useEffect, useCallback } from "react";
import PageHeader from "./PageHeader";
import { FarbtypDots } from "./farbtyp";
import { navigate } from "./router";
import {
  CheckCircle2, MessageSquare, Activity, Clock, FileText,
  Sparkles, ChevronRight, RotateCcw, Loader, ExternalLink, Pencil,
} from "lucide-react";

function fmt(d) {
  if (!d) return "";
  return new Date(d).toLocaleString("de-DE", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function fmtRel(d) {
  if (!d) return "";
  const diff = Date.now() - new Date(d).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "gerade eben";
  if (min < 60) return `vor ${min} Min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `vor ${h} Std`;
  const days = Math.floor(h / 24);
  return `vor ${days} T`;
}

/* ===================== Jotform-Posteingang ===================== */

// "**Aufgabe (30% der Arbeitszeit):** Beschreibung\nArbeitsweise: …" → strukturierte Aufgaben.
function parseTasks(aufgaben) {
  if (!aufgaben) return [];
  return aufgaben.split("\n\n").map((block) => {
    const lines = block.split("\n");
    const m = lines[0].match(/^\*\*Aufgabe\s*\(([^)]*)\):\*\*\s*(.*)$/);
    if (m) return { pct: m[1].trim(), desc: m[2].trim(), meta: lines.slice(1).join(" ").trim() };
    return { pct: "", desc: block.replace(/\*\*/g, "").trim(), meta: "" };
  }).filter((t) => t.desc);
}

function splitLines(text) {
  if (!text) return [];
  return text.split(/\n+/).map((l) => l.replace(/^[-•*\s]+/, "").replace(/\*\*/g, "").trim()).filter(Boolean);
}

function ListField({ title, text }) {
  const items = splitLines(text);
  return (
    <div className="inbox-section">
      <div className="inbox-section-title">{title}</div>
      {items.length ? (
        <ul className="inbox-list">{items.map((it, i) => <li key={i}>{it}</li>)}</ul>
      ) : <div className="inbox-empty-field">—</div>}
    </div>
  );
}

function InboxCard({ sub, onGenerate, onNavigate }) {
  const [open, setOpen] = useState(false);
  const [check, setCheck] = useState(null); // null | "loading" | {ok, warnings}
  const [pers, setPers] = useState(null);   // null | "loading" | "error" | data
  const [persWhyOpen, setPersWhyOpen] = useState(false); // Begründung ein-/ausklappen
  const gen = sub.generation;
  const status = gen ? gen.status : "new";
  const running = status === "pending" || status === "running";
  const done = status === "done";

  const runCheck = async () => {
    setCheck("loading");
    try {
      const r = await fetch(`/api/jotform/submissions/${sub.submissionId}/check`, {
        method: "POST", credentials: "include",
      });
      const j = await r.json().catch(() => ({}));
      setCheck(r.ok ? j : { ok: false, warnings: [{ issue: j.error || "Prüfung fehlgeschlagen", severity: "high" }] });
    } catch {
      setCheck({ ok: false, warnings: [{ issue: "Prüfung fehlgeschlagen", severity: "high" }] });
    }
  };

  const runPersonality = useCallback(async () => {
    setPers("loading");
    try {
      const r = await fetch(`/api/jotform/submissions/${sub.submissionId}/personality`, {
        method: "POST", credentials: "include",
      });
      const j = await r.json().catch(() => ({}));
      setPers(r.ok ? j : "error");
    } catch { setPers("error"); }
  }, [sub.submissionId]);

  // Persönlichkeitstyp automatisch kritisch prüfen, sobald das Formular geöffnet wird (einmalig, gecacht).
  useEffect(() => {
    if (open && pers === null) runPersonality();
  }, [open, pers, runPersonality]);

  const handleGenerate = (farbtyp) => {
    if (done && !window.confirm("Diese Einreichung wurde bereits generiert. Erneut generieren?")) return;
    onGenerate(sub, farbtyp);
  };

  const idata = sub.inputData || {};
  const tasks = open ? parseTasks(idata.aufgaben) : [];

  return (
    <div className="inbox-card">
      <div className="inbox-card-head">
        <button type="button" className="inbox-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          <ChevronRight size={15} className={open ? "inbox-chev open" : "inbox-chev"} />
        </button>
        <div className="inbox-title-wrap">
          <span className="inbox-title">{sub.stellentitel || "Ohne Titel"}</span>
          <span className="inbox-sub">
            {sub.unternehmen}{sub.standort ? ` · ${sub.standort}` : ""} · {fmtRel(sub.createdAt)}
          </span>
        </div>
        <span className={"inbox-badge inbox-badge--" + (done ? "done" : running ? "running" : status === "error" ? "error" : "new")}>
          {done ? <><CheckCircle2 size={12} /> Generiert</>
            : running ? <><Loader size={12} className="icon-spin" /> Läuft</>
            : status === "error" ? "Fehler"
            : "Neu"}
        </span>
      </div>

      <div className="inbox-actions">
        <button type="button" className="btn-secondary-sm" onClick={() => setOpen((o) => !o)}>
          {open ? "Formular ausblenden" : "Formular prüfen"}
        </button>
        {done && (
          <button type="button" className="btn-secondary-sm" onClick={() => onNavigate && onNavigate("jobs")}>
            Anzeige öffnen
          </button>
        )}
        <button type="button" className="btn-primary-sm" onClick={() => handleGenerate()} disabled={running}>
          {running ? <><Loader size={13} className="icon-spin" /> Generierung läuft …</>
            : done ? <><RotateCcw size={13} /> Neu generieren</>
            : <><Sparkles size={13} /> Anzeige generieren</>}
        </button>
      </div>

      {open && (
        <div className="inbox-detail">
          {/* Persönlichkeitstyp – kritisch geprüft */}
          <div className="inbox-section inbox-pers">
            <div className="inbox-section-title"><Sparkles size={13} /> Persönlichkeitstyp</div>
            {pers === "loading" && (
              <div className="inbox-pers-loading"><Loader size={13} className="icon-spin" /> Wird kritisch geprüft …</div>
            )}
            {pers === "error" && (
              <div className="inbox-pers-err">
                Prüfung fehlgeschlagen. <button type="button" className="inbox-link" onClick={runPersonality}>Erneut prüfen</button>
              </div>
            )}
            {pers && pers !== "loading" && pers !== "error" && (() => {
              const why = pers.reasoning || pers.assessment;
              return (
              <div className={"farbtyp-pers-result" + (pers.agreement ? " is-match" : " is-diff")}>
                <div className="farbtyp-pers-row">
                  <span className="farbtyp-pers-tag">Eingereicht</span>
                  <FarbtypDots value={pers.submitted?.key} />
                  <strong className="farbtyp-pers-name">{pers.submitted?.label}</strong>
                  {pers.agreement && <span className="farbtyp-pers-verdict ok"><CheckCircle2 size={13} /> passt</span>}
                </div>
                {!pers.agreement && (
                  <div className="farbtyp-pers-row farbtyp-pers-row--rec">
                    <span className="farbtyp-pers-tag">Empfohlen</span>
                    <FarbtypDots value={pers.recommended?.key} />
                    <strong className="farbtyp-pers-name">{pers.recommended?.label}</strong>
                    <button type="button" className="btn-secondary-sm farbtyp-pers-apply" disabled={running} onClick={() => handleGenerate(pers.recommended.key)}>
                      <Sparkles size={13} /> Mit „{pers.recommended.label}" generieren
                    </button>
                  </div>
                )}
                {why && (
                  <div className="farbtyp-pers-why">
                    <button type="button" className="farbtyp-pers-why-toggle" onClick={() => setPersWhyOpen((o) => !o)}>
                      <span className="farbtyp-why-chevron">{persWhyOpen ? "▾" : "▸"}</span> Warum diese Empfehlung?
                    </button>
                    {persWhyOpen && <p className="farbtyp-pers-text">{why}</p>}
                  </div>
                )}
                <div className="inbox-pers-foot">
                  <button type="button" className="inbox-link" onClick={runPersonality}>neu prüfen</button>
                </div>
              </div>
              );
            })()}
          </div>

          {/* KI-Vorabprüfung der Datenqualität – oben, vor den Formularinhalten */}
          <div className="inbox-section inbox-check">
            <button type="button" className="btn-secondary-sm" onClick={runCheck} disabled={check === "loading"}>
              {check === "loading" ? <><Loader size={13} className="icon-spin" /> Prüfe …</> : <><Sparkles size={13} /> KI-Vorabprüfung</>}
            </button>
            {check && check !== "loading" && (
              <div className="inbox-check-result">
                {check.ok && (!check.warnings || check.warnings.length === 0) ? (
                  <div className="inbox-check-ok"><CheckCircle2 size={14} /> Daten sehen vollständig aus.</div>
                ) : (
                  <>
                    {!check.ok && <div className="inbox-check-warn-head">Vor der Generierung prüfen:</div>}
                    {(check.warnings || []).map((w, i) => (
                      <div key={i} className={"inbox-warn inbox-warn--" + (w.severity || "low")}>
                        {w.field ? <strong>{w.field}: </strong> : null}{w.issue}
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Aufgaben als einzelne Karten */}
          <div className="inbox-section">
            <div className="inbox-section-title"><FileText size={13} /> Aufgaben</div>
            {tasks.length ? (
              <div className="inbox-tasks">
                {tasks.map((t, i) => (
                  <div className="inbox-task" key={i}>
                    {t.pct && <span className="inbox-task-pct">{t.pct}</span>}
                    <div className="inbox-task-body">
                      <div className="inbox-task-desc">{t.desc}</div>
                      {t.meta && <div className="inbox-task-meta">{t.meta}</div>}
                    </div>
                  </div>
                ))}
              </div>
            ) : <div className="inbox-empty-field">—</div>}
          </div>

          <ListField title="Stärken & Eigenschaften" text={idata.staerken} />
          <ListField title="Hard Skills" text={idata.hardSkills} />
          <ListField title="Akzeptable Schwächen" text={idata.schwaechen} />
          <ListField title="Benefits" text={idata.benefits} />
          <ListField title="Disqualifikation" text={idata.disqualifikation} />
          {idata.zusatzinfos && (
            <div className="inbox-section">
              <div className="inbox-section-title">Zusatzinfos</div>
              <div className="inbox-field-text">{idata.zusatzinfos}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ===================== Partner-Reaktionen (Share) ===================== */

const EVENT_META = {
  view: { Icon: Activity, label: "hat die Anzeige aufgerufen" },
  comment_seen: { Icon: MessageSquare, label: "hat einen Kommentar angesehen" },
  approval: { Icon: CheckCircle2, label: "hat die Anzeige freigegeben" },
  approval_revoked: { Icon: RotateCcw, label: "hat die Freigabe zurückgenommen" },
};

function ActivityCard({ item, onOpen }) {
  const approved = !!item.approved_at;
  const canOpen = !!(onOpen && item.partner_id && item.stellenanzeige_id);
  const shareUrl = item.token ? `${window.location.origin}/s/${item.token}` : null;
  // Klickbarer Name/Titel → zur zugehörigen Stellenanzeige. Fällt auf reinen
  // Text zurück, wenn die Anzeige nicht (mehr) verlinkbar ist.
  const NameLink = ({ children, className }) =>
    canOpen ? (
      <button
        type="button"
        className={"activity-namelink" + (className ? " " + className : "")}
        onClick={(e) => { e.stopPropagation(); onOpen(); }}
        title="Zur Stellenanzeige"
      >
        {children}
      </button>
    ) : (
      <span className={className}>{children}</span>
    );
  return (
    <div className="activity-card">
      <div className="activity-card-head">
        <span className="activity-card-icon"><FileText size={18} /></span>
        <div className="activity-card-title-wrap">
          <NameLink className="activity-card-title">{item.stellentitel || "Stellenanzeige"}</NameLink>
          {item.unternehmen && <span className="activity-card-sub">{item.unternehmen}</span>}
        </div>
        <span className={"activity-badge " + (approved ? "approved" : "pending")}>
          {approved ? (<><CheckCircle2 size={13} /> Freigegeben</>) : "Offen"}
        </span>
      </div>

      {approved && (
        <div className="activity-approved-line">
          <CheckCircle2 size={14} /> Freigegeben{item.approved_by ? (<> von <NameLink>{item.approved_by}</NameLink></>) : ""} · {fmt(item.approved_at)}
        </div>
      )}

      <div className="activity-stats">
        <span><Activity size={13} /> {item.views} Aufrufe</span>
        <span><MessageSquare size={13} /> {item.comments_open} offen / {item.comments_total} Kommentare</span>
        <span><Clock size={13} /> Letzte Aktivität {fmtRel(item.last_activity)}</span>
      </div>

      {item.events && item.events.length > 0 && (
        <div className="activity-log">
          {item.events.map((e, i) => {
            const m = EVENT_META[e.type] || EVENT_META.view;
            const Icon = m.Icon;
            const quote = e.quoted_text ? e.quoted_text.slice(0, 60) : "";
            return (
              <div key={i} className={"activity-log-row" + (e.type === "approval" ? " is-approval" : "")}>
                <Icon size={13} />
                <span className="activity-log-text">
                  <NameLink><strong>{e.author_name || "Jemand"}</strong></NameLink> {m.label}
                  {quote && (
                    <span className="activity-log-quote"> „{quote}{e.quoted_text.length > 60 ? "…" : ""}"</span>
                  )}
                </span>
                <span className="activity-log-time">{fmtRel(e.created_at)}</span>
              </div>
            );
          })}
        </div>
      )}

      {(shareUrl || canOpen) && (
        <div className="activity-card-actions">
          {shareUrl && (
            <a
              className="activity-action"
              href={shareUrl}
              target="_blank"
              rel="noreferrer"
            >
              <ExternalLink size={14} /> Share-Link öffnen
            </a>
          )}
          {canOpen && (
            <button type="button" className="activity-action" onClick={onOpen}>
              <Pencil size={14} /> Bearbeiten
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function ActivityPage({ onNavigate }) {
  const [inbox, setInbox] = useState([]);
  const [inboxLoading, setInboxLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadInbox = useCallback(() => {
    fetch("/api/jotform/inbox", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setInbox(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setInboxLoading(false));
  }, []);

  // Aus einer Aktivitätskarte zur zugehörigen Stellenanzeige springen.
  // Nutzt denselben Deep-Link-Weg wie die Jobs-Seite selbst: URL setzen +
  // popstate auslösen, damit App den Jobs-Tab aktiviert und das Detail lädt.
  const goToJob = useCallback((item) => {
    if (!item || !item.partner_id || !item.stellenanzeige_id) return;
    navigate(`/jobs/${item.partner_id}/${item.stellenanzeige_id}`);
  }, []);

  const loadActivity = useCallback(() => {
    fetch("/api/share-activity", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => { setItems(Array.isArray(d) ? d : []); setError(""); })
      .catch(() => setError("Aktivität konnte nicht geladen werden."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadInbox();
    const t = setInterval(loadInbox, 60000);
    return () => clearInterval(t);
  }, [loadInbox]);

  useEffect(() => {
    loadActivity();
    const t = setInterval(loadActivity, 15000);
    return () => clearInterval(t);
  }, [loadActivity]);

  const generate = useCallback(async (sub, farbtyp) => {
    setInbox((list) => list.map((s) =>
      s.submissionId === sub.submissionId ? { ...s, generation: { status: "running", stage: "agent1" } } : s
    ));
    try {
      const r = await fetch(`/api/jotform/submissions/${sub.submissionId}/generate-async`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(farbtyp ? { farbtyp } : {}),
      });
      if (!r.ok) throw new Error();
    } catch {
      setInbox((list) => list.map((s) =>
        s.submissionId === sub.submissionId ? { ...s, generation: sub.generation } : s
      ));
    }
    setTimeout(loadInbox, 4000);
  }, [loadInbox]);

  const newCount = inbox.filter((s) => !s.generation).length;
  const approvedCount = items.filter((i) => i.approved_at).length;

  return (
    <>
      <PageHeader
        eyebrow="Verwaltung"
        title="Aktivität"
        subtitle="Neue Jotform-Einreichungen, Freigaben und Reaktionen der Partner an einem Ort."
      />

      <section className="activity-section">
        <div className="activity-section-head">
          <FileText size={15} />
          <span>Neue Einreichungen</span>
          {newCount > 0 && <span className="activity-section-count">{newCount} neu</span>}
        </div>
        {inboxLoading ? (
          <div className="loading-indicator"><div className="spinner" /><span>Einreichungen laden …</span></div>
        ) : inbox.length === 0 ? (
          <div className="empty-state">Keine Jotform-Einreichungen gefunden.</div>
        ) : (
          <div className="inbox-list">
            {inbox.map((s) => (
              <InboxCard key={s.submissionId} sub={s} onGenerate={generate} onNavigate={onNavigate} />
            ))}
          </div>
        )}
      </section>

      <section className="activity-section">
        <div className="activity-section-head">
          <CheckCircle2 size={15} />
          <span>Freigaben &amp; Reaktionen</span>
          {!loading && items.length > 0 && (
            <span className="activity-section-count">{approvedCount} freigegeben</span>
          )}
        </div>
        {loading && (
          <div className="loading-indicator"><div className="spinner" /><span>Laden …</span></div>
        )}
        {error && <div className="empty-state">{error}</div>}
        {!loading && !error && items.length === 0 && (
          <div className="empty-state">
            Noch keine geteilten Stellenanzeigen. Sobald du eine Anzeige per Share-Link teilst,
            erscheinen hier Aufrufe, Kommentar-Reaktionen und Freigaben der Partner.
          </div>
        )}
        <div className="activity-list">
          {items.map((it) => <ActivityCard key={it.token} item={it} onOpen={() => goToJob(it)} />)}
        </div>
      </section>
    </>
  );
}
